import type React from "react";

/**
 * Decorative organic SVG shapes used as visual accents.
 * These are intentionally abstract — not icons, not illustrations.
 * They add warmth and personality without competing with content.
 */

/** Generates starburst polygon points for zigzag badge edges */
function starburstPoints(cx: number, cy: number, outerR: number, innerR: number, n: number): string {
  const pts: string[] = [];
  for (let i = 0; i < n * 2; i++) {
    const a = (i * Math.PI) / n - Math.PI / 2;
    const r = i % 2 === 0 ? outerR : innerR;
    pts.push(`${(cx + r * Math.cos(a)).toFixed(2)},${(cy + r * Math.sin(a)).toFixed(2)}`);
  }
  return pts.join(" ");
}

/** Generates a smooth rounded scallop path using quadratic bezier curves */
function roundedScallop(cx: number, cy: number, outerR: number, innerR: number, n: number): string {
  const pts: string[] = [];
  for (let i = 0; i < n; i++) {
    const a1 = (i * 2 * Math.PI / n) - Math.PI / 2;
    const a2 = ((i + 0.5) * 2 * Math.PI / n) - Math.PI / 2;
    const a3 = ((i + 1) * 2 * Math.PI / n) - Math.PI / 2;
    const x1 = (cx + innerR * Math.cos(a1)).toFixed(2);
    const y1 = (cy + innerR * Math.sin(a1)).toFixed(2);
    const cpx = (cx + outerR * Math.cos(a2)).toFixed(2);
    const cpy = (cy + outerR * Math.sin(a2)).toFixed(2);
    const x3 = (cx + innerR * Math.cos(a3)).toFixed(2);
    const y3 = (cy + innerR * Math.sin(a3)).toFixed(2);
    if (i === 0) pts.push(`M ${x1} ${y1}`);
    pts.push(`Q ${cpx} ${cpy} ${x3} ${y3}`);
  }
  return pts.join(" ") + " Z";
}

/** 4-pointed spark / star accent */
export function SparkStar({
  size = 24,
  color = "#F5C542",
  className = "",
  style,
}: {
  size?: number;
  color?: string;
  className?: string;
  style?: React.CSSProperties;
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      style={style}
      aria-hidden="true"
    >
      <path
        d="M12 2 L13.2 10.8 L22 12 L13.2 13.2 L12 22 L10.8 13.2 L2 12 L10.8 10.8 Z"
        fill={color}
      />
    </svg>
  );
}

/** Jagged sticker badge — red front with gray shadow offset, white text */
export function StickerBadge({
  label,
  className = "",
  style,
}: {
  label: string;
  className?: string;
  style?: React.CSSProperties;
}) {
  const pts = starburstPoints(50, 50, 48, 41, 22);
  const shadowPts = starburstPoints(50, 50, 48, 41, 22);
  const lines = label.split("\n");

  return (
    <div
      className={`pointer-events-none select-none ${className}`}
      style={style}
      aria-hidden="true"
    >
      <svg viewBox="0 0 100 100" className="w-full h-full" overflow="visible">
        {/* Shadow layer */}
        <g transform="translate(4,5) rotate(10, 50, 50)">
          <polygon points={shadowPts} fill="#9B96C0" />
        </g>
        {/* Front layer */}
        <polygon points={pts} fill="#FA543C" />
        {/* Bullet dot */}
        <circle cx="50" cy={lines.length > 1 ? "38" : "46"} r="1.8" fill="#F6E8E6" opacity="0.85" />
        {/* Label text */}
        {lines.length === 1 ? (
          <text
            x="50"
            y="53"
            textAnchor="middle"
            dominantBaseline="middle"
            fill="#F6E8E6"
            fontSize="8.5"
            fontFamily="Bricolage Grotesque, sans-serif"
            fontWeight="600"
            letterSpacing="0.02"
          >
            {lines[0]}
          </text>
        ) : (
          <>
            <text
              x="50"
              y="46"
              textAnchor="middle"
              dominantBaseline="middle"
              fill="#F6E8E6"
              fontSize="8.5"
              fontFamily="Bricolage Grotesque, sans-serif"
              fontWeight="600"
            >
              {lines[0]}
            </text>
            <text
              x="50"
              y="57"
              textAnchor="middle"
              dominantBaseline="middle"
              fill="#F6E8E6"
              fontSize="8.5"
              fontFamily="Bricolage Grotesque, sans-serif"
              fontWeight="600"
            >
              {lines[1]}
            </text>
          </>
        )}
      </svg>
    </div>
  );
}

/** Animated circular badge — "designed and built by me" with heart, rotates slowly.
 *  Badge shape is the exact Figma SVG path. Text arcs inside the pink circle. */
export function RotatingBadge({ className = "" }: { className?: string }) {
  // Badge center per the Figma viewBox (0 0 129 151)
  const cx = 64.182;
  const cy = 64.182;
  // Text arc inside the pink badge — radius 30 keeps it well inside
  const textRadius = 30;

  return (
    <div className={`pointer-events-none select-none ${className}`} aria-hidden="true">
      <svg
        viewBox="0 0 129 151"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="w-full h-full"
      >
        <defs>
          {/* Exact Figma drop-shadow filter: dy=22, dark navy at 40% */}
          <filter id="badge-shadow" x="0" y="0" width="128.364" height="150.364" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
            <feOffset dy="22" />
            <feComposite in2="hardAlpha" operator="out" />
            <feColorMatrix type="matrix" values="0 0 0 0 0.098 0 0 0 0 0.0824 0 0 0 0 0.2902 0 0 0 0.4 0" />
            <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow" />
            <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow" result="shape" />
          </filter>
          {/* Circular path for text arc — inside the pink badge */}
          <path
            id="badgeTextCircle"
            d={`M ${cx},${cy - textRadius} A ${textRadius},${textRadius} 0 1,1 ${cx - 0.01},${cy - textRadius}`}
          />
        </defs>

        {/* Exact Figma badge path with drop shadow */}
        <g filter="url(#badge-shadow)">
          <path
            d="M128.364 64.1819C128.364 69.6062 120.563 73.9326 119.226 78.9308C117.842 84.1071 122.394 91.746 119.772 96.2777C117.113 100.875 108.192 100.735 104.466 104.466C100.741 108.197 100.875 117.113 96.2777 119.772C91.746 122.394 84.1071 117.843 78.9308 119.226C73.9293 120.563 69.6029 128.364 64.1819 128.364C58.7608 128.364 54.4312 120.563 49.433 119.226C44.2566 117.843 36.6178 122.394 32.086 119.772C27.4887 117.113 27.6286 108.192 23.8977 104.466C20.1667 100.741 11.2507 100.875 8.59154 96.2777C5.96951 91.746 10.522 84.1071 9.13779 78.9308C7.80165 73.9293 0 69.6029 0 64.1819C0 58.7608 7.80165 54.4312 9.13779 49.433C10.522 44.2566 5.96951 36.6178 8.59154 32.086C11.2507 27.4887 20.1722 27.6286 23.8977 23.8977C27.6231 20.1667 27.4887 11.2507 32.086 8.59154C36.6178 5.96951 44.2566 10.522 49.433 9.13779C54.4345 7.80165 58.7619 0 64.1819 0C69.6018 0 73.9326 7.80165 78.9308 9.13779C84.1071 10.522 91.746 5.96951 96.2777 8.59154C100.875 11.2518 100.735 20.1722 104.466 23.8987C108.197 27.6253 117.113 27.4887 119.772 32.086C122.394 36.6178 117.842 44.2566 119.226 49.433C120.563 54.4312 128.364 58.7586 128.364 64.1819Z"
            fill="#FFADE3"
          />
        </g>

        {/* Rotating text arc — inside the pink circle, one pass */}
        <g className="spin-badge-ccw" style={{ transformOrigin: `${cx}px ${cy}px` }}>
          <text
            fontSize="9"
            fontFamily="Bricolage Grotesque, sans-serif"
            fontWeight="500"
            fill="#26103D"
            letterSpacing="2.5"
          >
            <textPath href="#badgeTextCircle">
              designed and built by me ·
            </textPath>
          </text>
        </g>

        {/* Exact Figma heart path */}
        <path
          d="M82.935 61.7251C82.935 61.5962 82.9459 61.4673 82.9459 61.3373C82.9459 56.1206 78.5452 51.5986 73.1187 51.5986C69.1496 51.5986 65.7334 54.1529 64.182 57.4075C62.6306 54.1529 59.2143 51.5986 55.2463 51.5986C49.8187 51.5986 45.4137 56.1206 45.4137 61.3373C45.4137 61.4673 45.4137 61.5962 45.4246 61.7251C45.4246 61.7251 44.8696 67.9219 51.7612 74.6878C53.5409 76.4708 55.4397 78.2035 57.8039 80.2258C59.5793 81.7445 61.839 82.5791 64.1754 82.5791C66.5119 82.5791 68.7714 81.7445 70.5469 80.2258C72.9111 78.2035 74.8077 76.4708 76.5896 74.6878C83.4889 67.9219 82.935 61.7251 82.935 61.7251Z"
          fill="#FA543C"
        />
      </svg>
    </div>
  );
}

export function HeroShapes() {
  return (
    <div className="absolute top-0 right-0 w-[480px] h-[480px] pointer-events-none overflow-hidden" aria-hidden="true">
      {/* Large blue blob */}
      <svg viewBox="0 0 400 400" className="absolute top-[-60px] right-[-80px] w-[340px] h-[340px] opacity-90">
        <path
          d="M320,60 C360,100 380,160 360,220 C340,280 280,320 220,330 C160,340 100,310 70,260 C40,210 50,140 90,100 C130,60 200,20 260,30 C300,36 300,36 320,60 Z"
          fill="#1D3FE8"
          opacity="0.12"
        />
      </svg>
      {/* Amber accent blob */}
      <svg viewBox="0 0 200 200" className="absolute top-[80px] right-[40px] w-[160px] h-[160px]">
        <path
          d="M140,30 C170,50 185,90 175,125 C165,160 130,180 95,178 C60,176 30,152 20,120 C10,88 25,48 55,30 C85,12 110,10 140,30 Z"
          fill="#C4772A"
          opacity="0.18"
        />
      </svg>
      {/* Pink dot accent */}
      <svg viewBox="0 0 80 80" className="absolute top-[200px] right-[160px] w-[60px] h-[60px]">
        <circle cx="40" cy="40" r="35" fill="#F07DC4" opacity="0.25" />
      </svg>
      {/* Small blue circle */}
      <svg viewBox="0 0 50 50" className="absolute top-[20px] right-[200px] w-[36px] h-[36px]">
        <circle cx="25" cy="25" r="22" fill="#1D3FE8" opacity="0.2" />
      </svg>
    </div>
  );
}

export function ManifestoDot({ color = "#1D3FE8", size = 24 }: { color?: string; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="10" fill={color} opacity="0.3" />
      <circle cx="12" cy="12" r="6" fill={color} opacity="0.6" />
    </svg>
  );
}

export function ArrowDivider() {
  return (
    <div className="flex flex-col items-center justify-center gap-2" aria-hidden="true">
      {/* Amber circle with arrow */}
      <div className="w-12 h-12 rounded-full bg-[#C4772A] flex items-center justify-center shadow-lg">
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path d="M4 10h12M10 4l6 6-6 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
    </div>
  );
}
